/**
 * ValuationSpotRates
 * Read-only display of the three standard Chilean reference rates
 * (USD Observado, EUR Observado, UF) for a selected date.
 * Data is published automatically by BCCH — not editable by users.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/auth/AuthContext';
import { valuationService } from '../../services/api/valuationService';

interface ReferenceRate {
  key: string;
  label: string;
  description: string;
  value: number | null;
}

interface ValuationSpotRatesProps {
  systemDate?: string;
}

const ValuationSpotRates: React.FC<ValuationSpotRatesProps> = ({ systemDate }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || '';

  const [date, setDate] = useState(systemDate || new Date().toISOString().split('T')[0]);
  const [rates, setRates] = useState<ReferenceRate[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRates = useCallback(async () => {
    if (!clientId || !date) return;
    setLoading(true);
    try {
      const response = await valuationService.getReferenceRates(clientId, date);
      setRates(response.data || []);
    } catch {
      setRates([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, date]);

  useEffect(() => {
    loadRates();
  }, [loadRates]);

  const formatValue = (rate: ReferenceRate): string => {
    if (rate.value === null) return '—';
    if (rate.key === 'uf') {
      return rate.value.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return rate.value.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  };

  return (
    <div>
      {/* Date selector */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <label style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {t('valuation.positions.valuationDate', 'Fecha de Valorización')}
        </label>
        <input
          type="date"
          value={date}
          onChange={e => setDate(e.target.value)}
          style={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            padding: '0.4rem 0.6rem',
            color: 'var(--text-primary)',
            fontSize: '0.85rem',
          }}
        />
      </div>

      {/* Rates table */}
      {loading ? (
        <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
          {t('common.loading', 'Loading...')}
        </p>
      ) : (
        <table style={{ borderCollapse: 'collapse', width: '100%', maxWidth: '480px' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-color)' }}>
              <th style={{ textAlign: 'left', padding: '0.5rem 1rem 0.5rem 0', fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                {t('valuation.spotRates.rate', 'Rate')}
              </th>
              <th style={{ textAlign: 'left', padding: '0.5rem 1rem 0.5rem 0', fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                {t('valuation.spotRates.pair', 'Pair')}
              </th>
              <th style={{ textAlign: 'right', padding: '0.5rem 0 0.5rem 1rem', fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                {t('valuation.spotRates.value', 'Value')}
              </th>
              <th style={{ textAlign: 'center', padding: '0.5rem 0 0.5rem 1rem', fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>
                {t('valuation.spotRates.status', 'Status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {rates.map(rate => (
              <tr key={rate.key} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '0.65rem 1rem 0.65rem 0', fontSize: '0.875rem', color: 'var(--text-primary)', fontWeight: 500 }}>
                  {rate.label}
                </td>
                <td style={{ padding: '0.65rem 1rem 0.65rem 0', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                  {rate.description}
                </td>
                <td style={{ padding: '0.65rem 0 0.65rem 1rem', fontSize: '0.875rem', color: 'var(--text-primary)', textAlign: 'right', fontFamily: 'monospace' }}>
                  {formatValue(rate)}
                </td>
                <td style={{ padding: '0.65rem 0 0.65rem 1rem', textAlign: 'center' }}>
                  <span style={{
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    color: rate.value !== null ? '#2ecc71' : '#ff9800',
                  }}>
                    {rate.value !== null
                      ? t('valuation.spotRates.available', '✓ Loaded')
                      : t('valuation.spotRates.unavailable', '✗ Not loaded')}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ValuationSpotRates;
